import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { viewAndPrintDocumentHead } from "../../../common/api/authApi";
import { normalizedLocale } from "../../../common/libs/normalizedLocale";
import {
  filterDocumentsByLocale,
  getProductGroupDisplayName,
  pickContractAndApplication,
} from "../../../common/libs/productGroupLocale";
import CheckBox from "../CheckBox/CheckBox";
import ModalButton from "../Buttons/ModalButtons";
import ModalComponent from "../ModalComponent/ModalWindow";
import Loader from "../Loader/Loader";
import style from "./ProductGroupSignModal.module.css";

export type ProductGroupSignFile = {
  filename?: string;
  userFilename?: string;
  fileName?: string;
  sha256Hash?: string;
  productGroupCode?: string;
  size?: number;
};

type ProductGroupSignModalProps = {
  isOpen: boolean;
  files?: ProductGroupSignFile[] | null;
  productGroups?: Array<{ code?: string; name?: string; nameRu?: string; nameKk?: string }>;
  onClose: () => void;
  onSign: () => void | Promise<void>;
  signing?: boolean;
  title?: string;
};

const ProductGroupSignModal = ({
  isOpen,
  files,
  productGroups = [],
  onClose,
  onSign,
  signing = false,
  title,
}: ProductGroupSignModalProps) => {
  const { t, i18n } = useTranslation();
  const locale = normalizedLocale(i18n.language);
  const [agreed, setAgreed] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setAgreed(false);
    }
  }, [isOpen, files]);

  const handleDownload = async (filename?: string, name?: string) => {
    if (!filename) return;
    try {
      setIsDownloading(true);
      const fileBlob = await viewAndPrintDocumentHead(filename);
      const downloadUrl = window.URL.createObjectURL(fileBlob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", `${name || filename}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Ошибка при скачивании файла:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const getGroupName = (code: string) => {
    const group = productGroups.find((item) => item.code === code);
    return getProductGroupDisplayName(group ?? { code }, locale);
  };

  const localeFiles = filterDocumentsByLocale(files, locale);
  const groupedFiles = localeFiles.reduce(
    (acc: Record<string, ProductGroupSignFile[]>, file) => {
      const code = file?.productGroupCode ?? "";
      if (!acc[code]) acc[code] = [];
      acc[code].push(file);
      return acc;
    },
    {},
  );

  return (
    <>
      {(isDownloading || signing) && <Loader />}
      <ModalComponent
        isOpen={isOpen}
        onClose={onClose}
        headerContent={
          <div>
            <div className={style.modalHeader}>
              <div className={style.modalTitle}>
                {title || t("registrationWithECP.contractJewelryGroup")}
              </div>
              <div className={style.modalDesc}>
                {t("registrationWithECP.connectionInstructions")}
              </div>
            </div>
            <div className={style.horLine} />
          </div>
        }
        content={
          <div className="flex flex-col gap-y-[20px]">
            <div className="flex w-full gap-x-[5px] items-center text-black">
              <img src="/assets/paperclip.svg" className="w-[20px]" alt="" />
              <div className={style.modalDesc}>
                {t("registrationWithECP.beforeSigning")}
              </div>
            </div>
            <div
              className={`flex flex-col gap-y-[10px] pl-[20px] text-black ${style.modalDesc}`}
            >
              {Object.entries(groupedFiles).map(([code, groupFiles]) => {
                const groupName = getGroupName(code);
                const suffix = groupName ? ` — ${groupName}` : "";
                const standardLabel = `${t("registrationWithECP.standardContract")}${suffix}`;
                const applicationLabel = `${t("registrationWithECP.applicationAgreement")}${suffix}`;
                const { contract, application } =
                  pickContractAndApplication(groupFiles);

                return (
                  <div key={code || "no-code"} className="flex flex-col gap-y-[6px]">
                    {contract && (
                      <a
                        href="#"
                        className="custom-link text-blue-500 hover:text-blue-700"
                        onClick={(e) => {
                          e.preventDefault();
                          handleDownload(contract.filename, standardLabel);
                        }}
                      >
                        {standardLabel}
                      </a>
                    )}
                    {application && (
                      <a
                        href="#"
                        className="custom-link text-blue-500 hover:text-blue-700"
                        onClick={(e) => {
                          e.preventDefault();
                          handleDownload(application.filename, applicationLabel);
                        }}
                      >
                        {applicationLabel}
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex gap-x-[5px] w-full items-center">
              <CheckBox
                id="product-group-sign-agree"
                checked={agreed}
                onChange={(e: any) => setAgreed(e.target.checked)}
              />
              <label className={`max-w-full text-black ${style.modalDesc}`}>
                {t("registrationWithECP.agreeWithTerms")}
              </label>
            </div>
          </div>
        }
        customContent={
          <div className="flex gap-[20px]">
            <ModalButton
              color="green"
              type="button"
              disabled={!agreed || signing}
              onClick={onSign}
            >
              {t("registrationWithECP.signAndSend")}
            </ModalButton>
            <ModalButton color="grey" type="button" onClick={onClose}>
              {t("mainButtons.close")}
            </ModalButton>
          </div>
        }
      />
    </>
  );
};

export default ProductGroupSignModal;
